import React, {
  ChangeEvent,
  ElementRef,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';

import { ChevronDown } from '../../icons';
import { FieldOption } from '../../utils/types';
import { StyledError } from './';

export interface Props {
  options: FieldOption[];
  placeholder?: string;
  required?: boolean;
  selected?: FieldOption;
  autoFocus?: boolean;
  prefillValue?: string;
  validator?: (value: string) => undefined | string; // True if valid, Error string if invalid
  onSelectedChanged?: (op: FieldOption | undefined) => void;
  onClearPrefill?: () => void;
}
export const Dropdown = forwardRef<{ validate: () => boolean; getValue: () => string }, Props>(
  (
    {
      options = [],
      placeholder,
      selected,
      required,
      autoFocus,
      prefillValue,
      onClearPrefill,
      onSelectedChanged,
      validator,
    }: Props,
    ref,
  ) => {
    const [currentSelected, setCurrentSelected] = useState<FieldOption>();
    const [error, setError] = useState<string>();
    const [isFocused, setIsFocused] = useState(false);
    const selectElementRef = useRef<HTMLSelectElement>(null);
    const styledErrorRef = useRef<ElementRef<typeof StyledError>>(null);

    const isUsingPrefillValue = prefillValue && !currentSelected;

    const validate = (val: string | undefined) => {
      const _err = validator?.(val || '');
      if (!_err) {
        setError(undefined);
        return true;
      }
      setError(_err);
      styledErrorRef.current?.shakeError();
      return false;
    };

    const onSelect = (e: ChangeEvent<HTMLSelectElement>) => {
      const value = e.target.value;
      const option = options.find((option) => option.optionLabel === value);
      onSelectedChanged?.(option);
      setCurrentSelected?.(option);
      validate(value);
      onClearPrefill?.();
    };

    useEffect(() => {
      setCurrentSelected(selected);
    }, [selected]);

    useEffect(() => {
      if (autoFocus) {
        selectElementRef.current?.focus();
      }
    }, [autoFocus, selectElementRef]);

    useImperativeHandle(ref, () => ({
      validate: () => validate(currentSelected?.optionLabel),
      getValue: () => currentSelected?.optionLabel || '',
    }));

    /**
     * Native HTML select doesn't allow full freedom in customization.
     * So the trick is to use a custom view and overlay it on top of the native select.
     * The native select will have opacity and the custom view will be shown.
     * The clickevents on overlay is disabled so that the events fall through to the native select.
     */
    return (
      <OuterWrapper>
        {/* Native select tag (hidden) */}
        <NativeSelect
          ref={selectElementRef}
          onChange={onSelect}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          aria-label={placeholder + ' dropdown'}
          value={isUsingPrefillValue ? prefillValue : currentSelected?.optionLabel}
          aria-required={required}
        >
          <option value="" selected>
            {placeholder}
          </option>
          {options.map((option) => {
            return (
              <option key={option.optionLabel} value={option?.optionLabel}>
                {option?.optionLabel}
              </option>
            );
          })}
        </NativeSelect>
        {/* Custom select tag selector */}
        <CustomSelect
          border={`${error || isFocused ? '2px' : '1px'} solid`}
          borderColor={
            error !== undefined
              ? '#e22b12'
              : isFocused
              ? 'rgba(0, 173, 239, 1)'
              : 'rgba(193, 193, 193, 1)'
          }
        >
          {currentSelected || isUsingPrefillValue ? (
            <>
              <LabelFloating>
                {placeholder}
                {required && <RedStar>*</RedStar>}
              </LabelFloating>
              <OptionLabel>{currentSelected?.optionLabel || prefillValue}</OptionLabel>
            </>
          ) : (
            <Label>
              {placeholder}
              {required && <RedStar>*</RedStar>}
            </Label>
          )}
          <StyledChevron>
            <ChevronDown />
          </StyledChevron>
        </CustomSelect>
        {error && <StyledError error={error} ref={styledErrorRef} />}
      </OuterWrapper>
    );
  },
);

const OptionLabel = styled.div`
  font-size: 14px;
`;

const RedStar = styled.span`
  color: red;
  padding-left: 2px;
`;

const StyledChevron = styled.div`
  position: absolute;
  top: 17px;
  right: 15px;
`;

const OuterWrapper = styled.div`
  position: relative;
`;

const Label = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.additions.content.color2};
  position: absolute;
  top: 17px;
  left: 11px;
  white-space: nowrap;
  text-align: left;
  text-overflow: ellipsis;
  overflow: hidden;
  max-width: 85%;
`;

const LabelFloating = styled.div`
  padding-top: 7px;
  font-size: 14px;
  color: ${({ theme }) => theme.additions.content.color2};
  white-space: nowrap;
  text-align: left;
  text-overflow: ellipsis;
  overflow: hidden;
  max-width: 90%;
`;

const NativeSelect = styled.select`
  width: 100%;
  height: 51px;
  opacity: 0;
  outline: none;
  border: none;
  background: white;
  color: black;
`;

const CustomSelect = styled.div<{ border: string; borderColor?: string }>`
  pointer-events: none;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 51px;
  border-radius: 8px;
  text-align: left;
  padding-left: 8px;
  box-sizing: border-box;
  font-size: 12px;
  background: white;
  border: ${({ border }) => border};
  border-color: ${({ borderColor }) => borderColor};
  color: ${({ theme }) => theme.additions.content.color1};
`;
